import { Components, Theme, alpha, darken } from '@mui/material';
import { ButtonProps } from '@/components/Button';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    inherit: false;
    primary: true;
    secondary: true;
    success: true;
    error: true;
    info: true;
    warning: true;
  }

  interface ButtonPropsSizeOverrides {
    medium: true;
    small: true;
    xsmall: true;
  }
}

const components: Components<Omit<Theme, 'components'>>['MuiButton'] = {
  styleOverrides: {
    contained: ({ theme: { palette }, ownerState }) => {
      const color = ownerState?.color || 'primary';
      return {
        backgroundColor: palette[color].A100,
        '&:hover': {
          backgroundColor: darken(palette[color].A100 as string, 0.1)
        },
        '&:active': {
          backgroundColor: darken(palette[color].A100 as string, 0.1)
        },
        '&:focused': {
          backgroundColor: darken(palette[color].A100 as string, 0.1),
          border: '2px solid',
          borderColor:
            palette.mode === 'light'
              ? palette[color].A60
              : darken(palette[color].A100 as string, 0.32)
        },
        '&.Mui-disabled': {
          color:
            palette.mode === 'light'
              ? palette.textColor.A0
              : palette.textColor.A90,
          backgroundColor:
            palette.mode === 'light'
              ? palette[color].A60
              : darken(palette[color].A100 as string, 0.1)
        }
      };
    },
    containedSecondary: ({ theme: { palette } }) => {
      return {
        backgroundColor:
          palette.mode === 'light'
            ? palette.secondary.A100
            : palette.secondary.A60,
        color:
          palette.mode === 'light'
            ? palette.textColor.A80
            : palette.textColor.A0,
        '&:hover': {
          backgroundColor:
            palette.mode === 'light'
              ? darken(palette.secondary.A100 as string, 0.05)
              : palette.secondary.A60
        },
        '&:active': {
          backgroundColor:
            palette.mode === 'light'
              ? darken(palette.secondary.A100 as string, 0.05)
              : palette.secondary.A60
        },
        '&:focused': {
          backgroundColor:
            palette.mode === 'light'
              ? darken(palette.secondary.A100 as string, 0.05)
              : palette.secondary.A60,
          border: '2px solid',
          borderColor:
            palette.mode === 'light'
              ? palette.grayBackground.A40
              : palette.grayBackground.A60
        },
        '&.Mui-disabled': {
          color: palette.textColor.A40,
          backgroundColor:
            palette.mode === 'light'
              ? palette.secondary.A100
              : palette.secondary.A60
        }
      };
    },
    outlined: ({ theme: { palette }, ownerState }) => {
      const color = ownerState?.color || 'primary';

      return {
        backgroundColor: 'transparent',
        color: palette[color].A100,
        border: '1px solid',
        borderColor: palette[color].A100,
        '&:hover': {
          backgroundColor:
            palette.mode === 'light' ? palette.secondary.A80 : 'transparent'
        },
        '&.Mui-disabled': {
          borderColor:
            palette.mode === 'light'
              ? palette[color].A60
              : alpha(palette[color].A100 as string, 0.3),
          color:
            palette.mode === 'light'
              ? palette[color].A60
              : alpha(palette[color].A100 as string, 0.3)
        }
      };
    },
    text: ({ theme: { palette }, ownerState }) => {
      const color = ownerState?.color || 'primary';
      return {
        backgroundColor:
          palette.mode === 'light' ? palette.secondary.A60 : 'transparent',
        color: palette[color].A100,
        border: 'none',
        '&:hover': {
          backgroundColor:
            palette.mode === 'light'
              ? palette.secondary.A80
              : alpha(palette.secondary.A100 as string, 0.7)
        },
        '&:active': {
          backgroundColor: 'transparent'
        },
        '&:focused': {
          backgroundColor: 'transparent'
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
          borderColor: 'none',
          color:
            palette.mode === 'light'
              ? palette[color].A60
              : alpha(palette[color].A100 as string, 0.3)
        }
      };
    }
  },
  variants: [
    // don't forget to handle loading indicator
    {
      props: { size: 'medium' },
      style: ({ theme }) => ({
        padding: theme.spacing(2.5, 4),
        fontSize: '14px',
        lineHeight: '28px',
        fontWeight: 600
      })
    },
    {
      props: { size: 'xsmall' },
      style: ({ theme }) => ({
        padding: theme.spacing(1, 4),
        fontSize: '14px',
        lineHeight: '24px',
        fontWeight: 400
      })
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        padding: theme.spacing(1.5, 4),
        fontSize: '14px',
        lineHeight: '28px',
        fontWeight: 600
      })
    },
    {
      props: { size: 'medium', isIcon: true } as ButtonProps,
      style: ({ theme }) => ({
        minWidth: 'unset',
        padding: theme.spacing(3.5, 3.5)
      })
    },
    {
      props: { size: 'small', isIcon: true } as ButtonProps,
      style: ({ theme }) => ({
        minWidth: 'unset',
        padding: theme.spacing(2.5, 2.5)
      })
    },
    {
      props: { size: 'xsmall', isIcon: true } as ButtonProps,
      style: ({ theme }) => ({
        minWidth: 'unset',
        padding: theme.spacing(1, 2)
      })
    },
    {
      props: { radius: 'medium' } as ButtonProps,
      style: {
        borderRadius: 8
      }
    }
  ]
};

export default { components };
